import React from 'react';
import {
  X,
  CheckCircle,
  Copy,
  Download,
  Clock,
  AlertTriangle,
  Info,
  Database,
  User2,
  ListChecks
} from 'lucide-react';

interface ValidationPopupProps {
  showModal: boolean;
  onClose: () => void;
  outputData: any;
  timeTaken: number;
  llmUsed: string;
  errorDetails: string | null;
  entityInfo: { entity: string; database: string };
}

const ValidationPopup: React.FC<ValidationPopupProps> = ({
  showModal,
  onClose,
  outputData,
  timeTaken,
  llmUsed,
  errorDetails,
  entityInfo,
}) => {
  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(outputData, null, 2)], {
      type: 'application/json',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'output_data.json';
    link.click();
  };

  const handleCopy = () => {
    const outputText = JSON.stringify(outputData, null, 2);
    navigator.clipboard.writeText(outputText).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const renderOutput = () => {
    if (Array.isArray(outputData)) {
      const keys = Object.keys(outputData[0] || {});
      return (
        <div className="overflow-auto max-h-64 border border-neutral-800 rounded-md">
          <table className="w-full text-xs text-gray-300 border-collapse">
            <thead className="bg-neutral-900 border-b border-neutral-800">
              <tr>
                {keys.map((key) => (
                  <th key={key} className="px-3 py-2 text-left font-medium text-gray-400">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {outputData.map((row: any, index: number) => (
                <tr key={index} className="hover:bg-neutral-800 border-b border-neutral-800">
                  {keys.map((key) => (
                    <td key={key} className="px-3 py-1">{row[key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      return (
        <pre className="text-xs bg-neutral-800 p-4 rounded-md overflow-auto max-h-64 border border-neutral-700">
          {JSON.stringify(outputData, null, 2)}
        </pre>
      );
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center ${!showModal && 'hidden'}`}
    >
      <div className="bg-neutral-900 text-white rounded-xl shadow-2xl ring-1 ring-neutral-700 w-full max-w-3xl px-6 py-6 relative border border-neutral-800 max-h-[85vh] overflow-y-auto">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="text-green-400 w-5 h-5" /> Validation Result
        </h2>

        {/* Metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-xs text-gray-300">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            Time Taken: <span className="font-medium">{timeTaken}s</span>
          </div>
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-gray-400" />
            LLM: <span className="font-medium">{llmUsed}</span>
          </div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-gray-400" />
            DB: <span className="font-medium">{entityInfo.database}</span>
          </div>
          <div className="flex items-center gap-2">
            <User2 className="w-4 h-4 text-gray-400" />
            Entity: <span className="font-medium">{entityInfo.entity}</span>
          </div>
        </div>

        {/* Logs */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-200 mb-2">Logs</h3>
          <div className="bg-neutral-800 p-3 rounded-md text-xs space-y-1 border border-neutral-700">
            <div className="text-gray-300"><span className="text-green-400">[INFO]</span> Process started...</div>
            <div className="text-yellow-400"><span className="text-yellow-500">[WARNING]</span> Latency exceeded threshold.</div>
            {errorDetails && (
              <div className="text-red-400"><span className="text-red-500">[ERROR]</span> {errorDetails}</div>
            )}
          </div>
        </div>

        {/* Output */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-200 mb-2 flex items-center gap-2">
            <Info className="text-blue-400 w-4 h-4" /> Output
          </h3>
          {renderOutput()}
        </div>

        {/* Violations */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-200 mb-2">Violations</h3>
          <div className="bg-blue-800 text-white text-xs p-3 rounded-md flex items-center gap-2 border border-blue-600">
            <AlertTriangle className="w-4 h-4" /> No violations detected. Agent is compliant.
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 justify-between items-center text-xs mb-4">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            <Download size={16} /> Download
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
          >
            <Copy size={16} /> Copy
          </button>
        </div>

        {/* Footer Close */}
        <div className="mt-6 text-right">
          <button
            onClick={onClose}
            className="bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-md text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationPopup;
